import React, { useEffect, useRef, useState } from "react";
import { Loader2, ShieldCheck } from "lucide-react";
import { BRAND } from "../mock";

const SDK_URL = "https://gateway.sumup.com/gateway/ecom/card/v2/sdk.js";
const API = process.env.REACT_APP_BACKEND_URL;
const GPAY_MID = process.env.REACT_APP_GOOGLE_PAY_MERCHANT_ID || "";

const loadSdk = () =>
  new Promise((resolve, reject) => {
    if (window.SumUpCard) return resolve();
    const existing = document.querySelector(`script[src="${SDK_URL}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("sdk")));
      return;
    }
    const s = document.createElement("script");
    s.src = SDK_URL;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("sdk"));
    document.head.appendChild(s);
  });

const SumUpWidget = ({ checkoutId, bookingId, amount, locale = "de-DE", onPaid, onFail }) => {
  const instance = useRef(null);
  const [msg, setMsg] = useState("Zahlungsformular wird geladen …");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    loadSdk()
      .then(() => {
        if (cancelled || !window.SumUpCard) return;
        setMsg("Zahlungsformular wird geladen …");
        const fallback = setTimeout(() => setLoading(false), 7000);
        instance.current = window.SumUpCard.mount({
          id: "sumup-card",
          checkoutId,
          currency: "CHF",
          locale,
          showSubmitButton: true,
          showFooter: true,
          // Apple Pay appears automatically on eligible Apple device/browser once the
          // domain is verified (domain-association file is served at /.well-known/).
          // Google Pay is offered here; merchantName is shown to the customer.
          googlePay: {
            merchantName: BRAND.name,
            ...(GPAY_MID ? { merchantId: GPAY_MID } : {}),
          },
          onPaymentMethodsLoad: (methods) => methods,
          onLoad: () => { clearTimeout(fallback); setLoading(false); setMsg(""); },
          onResponse: async (type, body) => {
            if (type === "sent" || type === "auth-screen") {
              setMsg("Zahlung wird autorisiert …");
            } else if (type === "invalid") {
              setMsg("Bitte überprüfen Sie Ihre Zahlungsdaten.");
            } else if (type === "error" || type === "fail") {
              setMsg("Zahlung fehlgeschlagen oder abgebrochen.");
              onFail && onFail();
            } else if (type === "success") {
              setMsg("Zahlung wird bestätigt …");
              try {
                const r = await fetch(`${API}/api/payments/${bookingId}`);
                const d = await r.json();
                if (d.payment_status === "PAID") {
                  onPaid && onPaid(d.ref || bookingId);
                } else {
                  setMsg(`Zahlungsstatus: ${d.payment_status || "unbekannt"}`);
                }
              } catch (e) {
                setMsg("Bestätigung fehlgeschlagen. Bitte kontaktieren Sie uns.");
              }
            }
          },
        });
      })
      .catch(() => {
        setLoading(false);
        setMsg("Zahlungsformular konnte nicht geladen werden.");
      });
    return () => {
      cancelled = true;
      try {
        instance.current && instance.current.unmount && instance.current.unmount();
      } catch (e) {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkoutId, bookingId]);

  return (
    <div className="space-y-4" data-testid="sumup-widget">
      <div className="flex items-center gap-2 text-[#5A607A]">
        <ShieldCheck size={18} className="text-[#25D366]" />
        <p className="font-body text-sm">
          Sichere Zahlung{amount ? ` · CHF ${amount}.–` : ""} — Karte, Apple&nbsp;Pay &amp; Google&nbsp;Pay.
        </p>
      </div>
      {loading && (
        <div className="flex items-center gap-2 text-[#5A607A]">
          <Loader2 size={16} className="animate-spin" /> <span className="font-body text-sm">{msg}</span>
        </div>
      )}
      <div id="sumup-card" className="rounded-2xl overflow-hidden" />
      {!loading && msg && <p className="font-body text-sm text-[#5A607A]" data-testid="sumup-status">{msg}</p>}
    </div>
  );
};

export default SumUpWidget;
